import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Landmark, Globe2, Sparkles, ShieldCheck, FileCheck2, Gauge, Target, ListChecks, Goal, ChevronRight, ExternalLink, Layers } from "lucide-react";
import { toast } from "sonner";

const RAG = { green: "text-[#00E65B]", amber: "text-[#FFB800]", red: "text-red-500", grey: "text-muted-foreground" };
const RAG_DOT = { green: "bg-[#00E65B]", amber: "bg-[#FFB800]", red: "bg-red-500", grey: "bg-muted-foreground/40" };

const STATUS_META = {
  not_started: { label: "Not started", tone: "danger", color: "text-red-500" },
  in_progress: { label: "In progress", tone: "warning", color: "text-[#FFB800]" },
  ready: { label: "Report-ready", tone: "lime", color: "text-[#00E65B]" },
  published: { label: "Published", tone: "success", color: "text-[#00E65B]" },
  not_applicable: { label: "N/A", tone: "outline", color: "text-muted-foreground" },
};
const STATUS_ORDER = ["not_started", "in_progress", "ready", "published", "not_applicable"];
const band = (p) => (p >= 70 ? "text-[#00E65B]" : p >= 45 ? "text-[#FFB800]" : "text-red-500");

function Stat({ label, value, unit, icon: Icon, testid, tone, sub, pkey, pp, pyear }) {
  return (
    <div className="card-flat p-4 group" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span>{pkey && <ProvenanceTrigger pkey={pkey} pp={pp} year={pyear} className="ml-auto" />}</div>
      <div className={`font-mono text-2xl font-bold tracking-tight ${tone || ""}`}>{value}<span className="text-xs text-muted-foreground font-sans ml-1">{unit}</span></div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function ReportingReadiness({ regime, base, eyebrow, title, subtitle, icon: HeadIcon, prefix }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [items, setItems] = useState([]);
  const [ai, setAi] = useState("");
  const [busy, setBusy] = useState(false);
  const [sdgSel, setSdgSel] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.get(`${base}/dashboard`), api.get(`${base}/list`)])
      .then(([dd, ll]) => { setD(dd.data); setItems(ll.data); }).catch(() => setD({ empty: true }));
  }, [base]);
  useEffect(() => { setD(null); setAi(""); load(); }, [load]);

  const genAi = async () => { setBusy(true); try { const r = await api.post(`${base}/ai/readiness-summary`); setAi(r.data.summary); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const setStatus = async (id, status) => {
    const cov = status === "published" ? 100 : status === "ready" ? 90 : status === "not_applicable" ? 0 : undefined;
    try { await api.patch(`${base}/item/${id}`, { status, ...(cov !== undefined ? { coverage_pct: cov } : {}) }); load(); toast.success("Updated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading readiness…</div>;
  if (d.empty) return <div className="p-8"><div className="card-flat p-12 text-center"><h2 className="font-display text-2xl font-bold">No reporting requirements yet.</h2><p className="text-muted-foreground mt-2">Requirements are mapped automatically from your profile and disclosure obligations.</p></div></div>;

  const sig = d.signals;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid={`${prefix}-page`}>
      <div>
        <div className="label-eyebrow mb-1">{eyebrow}</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><HeadIcon className="h-8 w-8 text-accent" /> {title}</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">{subtitle}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Overall readiness" value={d.overall_readiness} unit="%" icon={Gauge} testid={`${prefix}-stat-readiness`} pkey="reporting.readiness" pp={regime} tone={band(d.overall_readiness)} />
        <Stat label="Mandatory ready" value={`${d.mandatory.ready}/${d.mandatory.total}`} unit="" icon={ShieldCheck} testid={`${prefix}-stat-mandatory`} pkey="reporting.mandatory" pp={regime} sub={`${d.mandatory.pct}%`} />
        <Stat label="Requirements" value={d.total_items} unit="" icon={ListChecks} testid={`${prefix}-stat-items`} pkey="reporting.items" pp={regime} sub={`${d.applicable_items} applicable`} />
        <Stat label="Reports drafted" value={sig.reports_generated} unit="" icon={FileCheck2} pkey="reports.drafted" />
        <Stat label="Targets set" value={sig.targets} unit="" icon={Target} pkey="sys.targets" />
        <Stat label="Total emissions" value={formatNum(sig.emissions.total)} unit="tCO₂e" icon={Globe2} pkey="sys.emissions_total" sub={`DQ ${sig.data_quality_pct}%`} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid={`${prefix}-tab-overview`}>Overview</TabsTrigger>
          <TabsTrigger value="requirements" data-testid={`${prefix}-tab-requirements`}>Requirements</TabsTrigger>
          {regime === "international" && <TabsTrigger value="sdg" data-testid={`${prefix}-tab-sdg`}>UN SDG Mapping</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="card-flat p-4">
              <div className="label-eyebrow mb-3">Framework readiness</div>
              <div className="space-y-2.5">
                {d.frameworks.map((f) => (
                  <div key={f.framework}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="inline-flex items-center gap-1.5">{f.framework}{f.mandatory && <Badge variant="outline" className="text-[8px] py-0">Mandatory</Badge>}</span>
                      <span className={`font-mono ${band(f.coverage_pct)}`}>{f.coverage_pct}%</span>
                    </div>
                    <Progress value={f.coverage_pct} className="h-1.5" />
                  </div>
                ))}
              </div>
            </div>
            <div className="card-flat p-4 space-y-3">
              <div className="label-eyebrow mb-1">Status distribution</div>
              <div className="space-y-2">
                {STATUS_ORDER.filter((s) => d.by_status[s] > 0).map((s) => (
                  <div key={s}><div className="flex justify-between text-xs mb-1"><span className={STATUS_META[s].color}>{STATUS_META[s].label}</span><span className="font-mono">{d.by_status[s]}</span></div>
                    <Progress value={(d.by_status[s] / d.total_items) * 100} className="h-1.5" /></div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Scope 1/2/3</span><span className="font-mono">{sig.emissions.scope1}/{sig.emissions.scope2}/{sig.emissions.scope3}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Scope 3 cats</span><span className="font-mono">{sig.scope3_categories}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Materiality</span><span className="font-mono">{sig.materiality_done ? "Done" : "Pending"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Carbon tax</span><span className="font-mono">{sig.carbon_tax_taxable ? "Taxable" : "Below"}</span></div>
              </div>
            </div>
          </div>
          <div className="card-flat p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI readiness summary</div>
              <Button size="sm" variant="primary" onClick={genAi} disabled={busy} data-testid={`${prefix}-ai`}>{busy ? "Generating…" : "Generate"}</Button>
            </div>
            {ai ? <p className="text-sm leading-relaxed" data-testid={`${prefix}-ai-text`}>{ai}</p> : <p className="text-sm text-muted-foreground">Generate a management summary of disclosure readiness, mandatory gaps and the recommended next action.</p>}
          </div>
        </TabsContent>

        <TabsContent value="requirements" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Framework</TableHead><TableHead>Requirement</TableHead><TableHead>Category</TableHead>
                <TableHead>Owner</TableHead><TableHead className="text-right">Coverage</TableHead><TableHead>Due</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((i) => (
                  <TableRow key={i.id} data-testid={`${prefix}-row-${i.id}`}>
                    <TableCell className="text-xs font-medium">{i.framework}{i.mandatory && <span className="text-[9px] text-accent ml-1">●</span>}</TableCell>
                    <TableCell className="text-sm">{i.synthetic && <Badge variant="warning" className="mr-1 text-[9px]">Synthetic</Badge>}{i.requirement}<div className="text-[10px] text-muted-foreground">{i.note}</div></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{i.category}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{i.owner}</TableCell>
                    <TableCell className="text-right"><span className={`font-mono text-xs ${band(i.coverage_pct)}`}>{i.coverage_pct}%</span></TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{i.due_period}</TableCell>
                    <TableCell>
                      <Select value={i.status} onValueChange={(v) => setStatus(i.id, v)}>
                        <SelectTrigger className="h-7 w-[140px] text-xs" data-testid={`${prefix}-status-${i.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {regime === "international" && (
          <TabsContent value="sdg" className="mt-4">
            <p className="text-xs text-muted-foreground mb-3">Click a goal to drill down into the material issues and KPIs supporting it — with originating standards, KPI links and an AI business-value narrative.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(d.sdgs || []).map((s) => (
                <button key={s.requirement} onClick={() => setSdgSel(s.requirement)} className="card-flat p-4 text-left hover:border-accent/60 hover:bg-surface-hover transition-colors group" data-testid={`${prefix}-sdg-card`}>
                  <div className="flex items-start gap-2 mb-2">
                    <Goal className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                    <span className="text-sm font-medium leading-tight flex-1">{s.requirement}</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors" />
                  </div>
                  <div className="flex justify-between text-xs mb-1"><Badge variant={STATUS_META[s.status].tone} className="text-[9px]">{STATUS_META[s.status].label}</Badge><span className={`font-mono ${band(s.coverage_pct)}`}>{s.coverage_pct}%</span></div>
                  <Progress value={s.coverage_pct} className="h-1.5" />
                  <div className="text-[10px] text-accent mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Drill down →</div>
                </button>
              ))}
              {(!d.sdgs || !d.sdgs.length) && <div className="text-sm text-muted-foreground p-4">No SDG mappings yet.</div>}
            </div>
            <SdgDrilldown base={base} requirement={sdgSel} prefix={prefix} onClose={() => setSdgSel(null)} navigate={navigate} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export function ReportingLocal() {
  return <ReportingReadiness regime="local" base="/reporting/local" prefix="rep-local"
    eyebrow="Reporting & Compliance · Local" title="Local Reporting" icon={Landmark}
    subtitle="Singapore regulatory readiness — SGX Core ESG Metrics, Rule 711A/B, ACRA–SGX ISSB roadmap, Carbon Pricing Act and NEA energy obligations." />;
}

export function ReportingInternational() {
  return <ReportingReadiness regime="international" base="/reporting/international" prefix="rep-intl"
    eyebrow="Reporting & Compliance · International" title="International Reporting" icon={Globe2}
    subtitle="Global standards readiness — IFRS S1/S2, GRI, SASB, TCFD, SBTi, CDP, GHG Protocol and UN SDG mapping." />;
}

function SdgDrilldown({ base, requirement, prefix, onClose, navigate }) {
  const [detail, setDetail] = useState(null);
  const [narrative, setNarrative] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!requirement) { setDetail(null); setNarrative(""); return; }
    setDetail(null); setNarrative("");
    api.get(`${base}/sdg/detail`, { params: { requirement } })
      .then((r) => setDetail(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [requirement, base]);

  const genNarrative = async () => {
    setBusy(true);
    try { const r = await api.post(`${base}/sdg/ai`, { requirement }); setNarrative(r.data.narrative); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!requirement} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid={`${prefix}-sdg-drilldown`}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Goal className="h-5 w-5 text-accent" /> {requirement}</DialogTitle>
        </DialogHeader>
        {!detail ? <div className="py-10 text-center text-muted-foreground text-sm">Loading drill-down…</div> : (
          <div className="space-y-5">
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <MiniStat label="Coverage" value={`${detail.coverage_pct}%`} tone={band(detail.coverage_pct)} />
              <MiniStat label="Supporting KPIs" value={detail.summary.kpi_count} />
              <MiniStat label="On / at-risk / off" value={`${detail.summary.on_track}/${detail.summary.at_risk}/${detail.summary.off_track}`} />
              <MiniStat label="Avg KPI score" value={detail.summary.avg_score ?? "—"} tone={detail.summary.avg_score != null ? band(detail.summary.avg_score) : ""} />
            </div>

            {/* AI narrative */}
            <div className="card-flat p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI business-value narrative</div>
                <Button size="sm" variant="primary" onClick={genNarrative} disabled={busy} data-testid={`${prefix}-sdg-ai-btn`}>{busy ? "Generating…" : "Generate"}</Button>
              </div>
              {narrative ? <p className="text-sm leading-relaxed" data-testid={`${prefix}-sdg-ai-text`}>{narrative}</p>
                : <p className="text-sm text-muted-foreground">Generate a board-level explanation of why this goal is material to the organisation, and which targets &amp; KPIs are advancing or holding back progress.</p>}
            </div>

            {/* Material issues */}
            <div>
              <div className="label-eyebrow inline-flex items-center gap-1 mb-2"><Layers className="h-3.5 w-3.5 text-accent" /> Material issues supporting this goal</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {detail.material_issues.map((m) => (
                  <div key={m.material_issue} className="rounded-sm border border-border p-3" data-testid={`${prefix}-sdg-issue`}>
                    <div className="flex items-center justify-between"><span className="text-sm font-medium">{m.material_issue}</span>
                      <span className={`font-mono text-xs ${m.avg_score != null ? band(m.avg_score) : "text-muted-foreground"}`}>{m.avg_score ?? "—"}</span></div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{m.kpi_count} KPI{m.kpi_count === 1 ? "" : "s"}</div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {m.standards.map((st) => <Badge key={st} variant="outline" className="text-[9px] py-0">{st}</Badge>)}
                    </div>
                  </div>
                ))}
                {!detail.material_issues.length && <div className="text-sm text-muted-foreground">No mapped material issues with KPIs yet.</div>}
              </div>
            </div>

            {/* KPIs */}
            <div>
              <div className="label-eyebrow inline-flex items-center gap-1 mb-2"><Target className="h-3.5 w-3.5 text-accent" /> KPIs &amp; targets affecting progress</div>
              <div className="card-flat overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>KPI</TableHead><TableHead>Standards (origin)</TableHead>
                    <TableHead className="text-right">Baseline → Current → Target</TableHead>
                    <TableHead className="text-right">Progress</TableHead><TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {detail.kpis.map((k) => (
                      <TableRow key={k.id} data-testid={`${prefix}-sdg-kpi-${k.id}`}>
                        <TableCell className="text-sm">
                          <div className="flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${RAG_DOT[k.rag] || RAG_DOT.grey}`} /><span className="font-medium">{k.kpi_name}</span></div>
                          <div className="text-[10px] text-muted-foreground ml-3.5">{k.material_issue} · {k.owner || "—"}</div>
                        </TableCell>
                        <TableCell className="max-w-[180px]"><div className="flex flex-wrap gap-1">{(k.frameworks || []).map((f) => <Badge key={f} variant="outline" className="text-[9px] py-0">{f}</Badge>)}</div></TableCell>
                        <TableCell className="text-right font-mono text-xs whitespace-nowrap">
                          {formatNum(k.baseline_value)} <span className="text-muted-foreground">→</span> {formatNum(k.current_value)} <span className="text-muted-foreground">→</span> {formatNum(k.target_value)} <span className="text-[10px] text-muted-foreground">{k.unit}</span>
                        </TableCell>
                        <TableCell className="text-right"><span className={`font-mono text-xs ${RAG[k.rag] || ""}`}>{k.progress_pct != null ? `${k.progress_pct}%` : "—"}</span></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={() => { onClose(); navigate(`/esg-scorecard?kpi=${encodeURIComponent(k.kpi_name)}`); }} data-testid={`${prefix}-sdg-open-kpi-${k.id}`}>Open <ExternalLink className="h-3 w-3" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!detail.kpis.length && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No KPIs mapped to this goal yet.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div className="rounded-sm border border-border p-2.5">
      <div className="label-eyebrow text-[9px] text-muted-foreground">{label}</div>
      <div className={`font-mono text-lg font-bold ${tone || ""}`}>{value}</div>
    </div>
  );
}
